import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getActionErrorMessage } from '../utils/errorHandler';
import "../styles/StudentChoiceForm.css";
import LoadingSpinner from './LoadingSpinner';

const StudentChoiceForm = () => {
    const [choices, setChoices] = useState(Array(8).fill().map(() => Array(4).fill("")));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [specialities, setSpecialities] = useState([]);
    const [config, setConfig] = useState(null);
    const [isRegistrationPeriod, setIsRegistrationPeriod] = useState(false);
    const [currentYear, setCurrentYear] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Get current curriculum
                const now = new Date();
                const curriculumYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
                setCurrentYear(curriculumYear);

                // Fetch configuration for current curriculum
                const configResponse = await axios.get(
                    getApiUrl(`config/get_configurations/${curriculumYear}`),
                    {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                        }
                    }
                );
                setConfig(configResponse.data);

                // Check if current time is within registration period
                const registrationStart = new Date(configResponse.data.form_registration?.startDate);
                const registrationEnd = new Date(configResponse.data.form_registration?.endDate);
                const isWithinRegistration = now >= registrationStart && now <= registrationEnd;
                setIsRegistrationPeriod(isWithinRegistration);

                // Only fetch data if within registration period
                if (isWithinRegistration) {
                    // Fetch specialities and choices
                    const [specialitiesResponse, choicesResponse] = await Promise.all([
                        axios.get(getApiUrl('crud/get_all_specialities'), {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                            }
                        }),
                        axios.get(getApiUrl('crud/get_student_choices'), {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                            }
                        })
                    ]);

                    setSpecialities(specialitiesResponse.data);
                    if (choicesResponse.data && choicesResponse.data.length > 0) {
                        setChoices(choicesResponse.data);
                    }
                }
            } catch (err) {
                setError(getActionErrorMessage(err, "fetch data"));
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await axios.put(
                getApiUrl('crud/student/choices/'),
                choices,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            setSuccess('Rotation preferences updated successfully!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(getActionErrorMessage(err, "update preferences"));
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (rotationIndex, choiceIndex, specialityId) => {
        const newChoices = [...choices];
        newChoices[rotationIndex][choiceIndex] = specialityId;
        setChoices(newChoices);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    const getSpecialityName = (specialityId) => {
        const speciality = specialities.find(spec => spec._id === specialityId);
        return speciality ? speciality.speciality : 'Unknown Speciality';
    };

    if (loading && specialities.length === 0) {
        return (
            <div className="speciality-institution-manager">
                <div className="manager-header">
                    <h2 className="manager-title">Student Choice Manager</h2>
                    <p className="manager-subtitle">Select your preferred preceptors and specialities</p>
                </div>
                <div className="loading-state">
                    <LoadingSpinner text="Loading your preferences..." variant="inline" />
                </div>
            </div>
        );
    }

    return (
        <div className="speciality-institution-manager">
            <div className="manager-header">
                <h2 className="manager-title">Student Choice Manager</h2>
                <p className="manager-subtitle">Select your preferred preceptors and specialities for each rotation</p>
            </div>

            {/* Success/Error Messages */}
            {success && (
                <div className="alert alert-success">
                    <span className="alert-icon">✅</span>
                    {success}
                </div>
            )}

            {error && (
                <div className="alert alert-error">
                    <span className="alert-icon">⚠️</span>
                    {error}
                </div>
            )}

            <div className="tab-content">
                <div className="create-form-section">
                    <h3 className="section-title">Registration Status</h3>
                    {!isRegistrationPeriod ? (
                        <div className="closed-registration-message">
                            <div className="registration-status">
                                <div className="status-icon">🔒</div>
                                <h4>Registration Period Closed</h4>
                                <p>
                                    The registration period for curriculum {currentYear}/{currentYear + 1} is from{' '}
                                    {config?.form_registration ? formatDate(config.form_registration.startDate) : 'N/A'} to{' '}
                                    {config?.form_registration ? formatDate(config.form_registration.endDate) : 'N/A'}.
                                </p>
                                <p className="status-note">Please check back during the registration period to submit your preferences.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="registration-status open">
                            <div className="status-icon">✅</div>
                            <h4>Registration Period Open</h4>
                            <p>You can now submit your rotation preferences for the current curriculum period.</p>
                            <div className="period-info">
                                <span className="period-label">Current Period:</span>
                                <span className="period-value">{currentYear}/{currentYear + 1}</span>
                            </div>
                        </div>
                    )}

                    <div className="choice-instructions">
                        <h4>How to Use This Form</h4>
                        <p>For each rotation, rank your preferred specialities from 1 (most preferred) to 4 (least preferred).</p>
                        <div className="instruction-steps">
                            <div className="step">
                                <span className="step-number">1</span>
                                <span className="step-text">Select your most preferred speciality for Choice 1</span>
                            </div>
                            <div className="step">
                                <span className="step-number">2</span>
                                <span className="step-text">Continue ranking down to Choice 4</span>
                            </div>
                            <div className="step">
                                <span className="step-number">3</span>
                                <span className="step-text">Repeat for all 8 rotations</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="list-section">
                    <h3 className="section-title">Rotation Preferences ({choices.length} rotations)</h3>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="items-grid">
                            {Array.from({ length: 8 }, (_, rotationIndex) => (
                                <div key={rotationIndex} className="modern-item-card choice-card">
                                    <div className="item-preview">
                                        <div className="item-header">
                                            <div className="item-main-info">
                                                <h4 className="item-name">Rotation {rotationIndex + 1}</h4>
                                                {config?.rotations && config.rotations[rotationIndex] && (
                                                    <div className="rotation-dates">
                                                        <span className="date-label">Period:</span>
                                                        <span className="date-value">
                                                            {formatDate(config.rotations[rotationIndex].startDate)} - {formatDate(config.rotations[rotationIndex].endDate)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="rotation-status">
                                                <span className="status-label">Status:</span>
                                                <span className={`status-value ${choices[rotationIndex].some(choice => choice !== "") ? 'completed' : 'pending'}`}>
                                                    {choices[rotationIndex].some(choice => choice !== "") ? 'Preferences Set' : 'No Preferences'}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className="choices-grid">
                                            {[0, 1, 2, 3].map(choiceIndex => (
                                                <div key={choiceIndex} className="choice-item">
                                                    <label 
                                                        htmlFor={`rotation${rotationIndex}choice${choiceIndex}`}
                                                        className="choice-label"
                                                    >
                                                        Choice {choiceIndex + 1}
                                                    </label>
                                                    <div className="choice-input-group">
                                                        <select
                                                            id={`rotation${rotationIndex}choice${choiceIndex}`}
                                                            value={choices[rotationIndex]?.[choiceIndex] || ""}
                                                            onChange={(e) => handleChange(rotationIndex, choiceIndex, e.target.value)}
                                                            className="choice-select"
                                                            disabled={!isRegistrationPeriod}
                                                        >
                                                            <option value="">Select Speciality</option>
                                                            {specialities.map(spec => (
                                                                <option key={spec._id} value={spec._id}>
                                                                    {spec.speciality}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {choices[rotationIndex]?.[choiceIndex] && (
                                                            <div className="selected-speciality">
                                                                <span className="speciality-chip">
                                                                    {getSpecialityName(choices[rotationIndex][choiceIndex])}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <button 
                            type="submit" 
                            className="submit-btn"
                            disabled={loading || !isRegistrationPeriod}
                        >
                            {loading ? 'Updating Preferences...' : 'Save All Rotation Preferences'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default StudentChoiceForm;